<script lang="ts">
    
    import { createEventDispatcher } from 'svelte';    
    import { fly, fade } from 'svelte/transition';
    import type { Answer, Question } from "../../global";

    type AnswerState = {
        answer: Answer;
        isWin: boolean;
        isFail: boolean;
    };
    
    export let question: Question | null = null;

    const dispatch = createEventDispatcher();
    
    let currentQuestion: Question | null;
    let showQuestion = false;
    let answerStates: AnswerState[];
    let canAnswer = false;

    $: updateQuestion(question);

    function updateQuestion(question: Question | null): void {
        canAnswer = false;
        if (!showQuestion) {
            setCurrentQuestion(question);
        } else {
            showQuestion = false;
        }
    }

    function setCurrentQuestion(question: Question | null): void {
        if (!question) {
            currentQuestion = null;
            showQuestion = false;
            canAnswer = false;
            return;
        }

        answerStates = question.answers.map(answer => ({
            answer,
            isFail: false,
            isWin: false,
        }));

        answerStates.sort(() => -0.5 + Math.random());

        currentQuestion = question;
        showQuestion = true;
        canAnswer = true;
    }

    function onAnswer(answerState: AnswerState): void {
        if (!canAnswer) {
            return;
        }

        canAnswer = false;

        if (answerState.answer.right) {
            answerState.isWin = true;
            dispatch('win', answerState.answer);
        } else {
            answerState.isFail = true;
            setTimeout(() => {
                const rightAnwerState = answerStates.find(({ answer }) => !!answer.right);
                if (rightAnwerState) {
                    rightAnwerState.isWin = true;
                }
                answerStates = answerStates;
            }, 600);
            dispatch('fail', answerState.answer);
        }
        answerStates = answerStates;
    }

    function questionHandler(questionWrapper: Element) {
        return {
            destroy() {
                setCurrentQuestion(question);
            }
        };
    }

</script>

<style lang="scss">

    @use '../../variables';

    .question {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .statement {
        color: #fff;
        font-weight: bold;
        font-family: 'Grandstander', cursive;
        text-align: center;
        font-size: 1.6rem;
        flex: 1 1 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .answer {
        position: relative;
        font-weight: bold;
        background-color: #fff;
        padding: 1rem;
        text-align: center;
        border-radius: 1000px;
        margin-top: 1rem;
        overflow: hidden;
        color: #707070;
        font-size: 1.1rem;

        &::before {
            content: '';
            display: block;
            position: absolute;
            left: 50%;
            top: 0;
            width: 0;
            height: 100%;
            transition-property: left, width;
            transition-duration: 400ms;
        }

        &.is-win, &.is-fail {
            color: #fff;

            &::before {
                left: -20px;
                width: calc(100% + 40px);
                transform: skewX(-15deg);
            }
        }

        &.is-win {

            &::before {
                background-color: variables.$answer-win-color;
            }
        }

        &.is-fail {

            &::before {
                background-color: variables.$answer-fail-color;
            }

        }

        .text {
            position: relative;
            z-index: 2;
        }
    }

</style>

{#if showQuestion}
    <div class="question" use:questionHandler>
        <div class="statement">
            <div class="text" transition:fade>{currentQuestion.statement}</div>
        </div>
        <div class="answers">
            {#each answerStates as answerState, index}
                <div
                    class="answer shadow"
                    class:is-win={answerState.isWin}
                    class:is-fail={answerState.isFail}
                    on:click={() => onAnswer(answerState)}
                    in:fly={{ x: 300, delay: 50 * index }}
                    out:fly={{ x: -300, delay: 50 * index }}>
                        <span class="text">{answerState.answer.text}</span>
                </div>
            {/each}
        </div>
    </div>
{/if}

